import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { MessageCircle, RefreshCcw, ArrowLeft, CheckCircle2, AlertCircle, Clock, ChevronRight } from "lucide-react";
import { toast } from "sonner";

const STATUS_COLOR = {
  sent:      { bg: "#dcfce7", fg: "#15803d", label: "Enviadas" },
  delivered: { bg: "#bfdbfe", fg: "#1d4ed8", label: "Entregues" },
  read:      { bg: "#c4b5fd", fg: "#5b21b6", label: "Lidas" },
  queued:    { bg: "#fef3c7", fg: "#a16207", label: "Na fila" },
  failed:    { bg: "#fee2e2", fg: "#dc2626", label: "Falhas" },
};

export default function WhatsAppJobs() {
  const { apiCall } = useAuth();
  const navigate = useNavigate();
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await apiCall("get", "/whatsapp/campaigns/jobs?limit=50");
      setJobs(r.data || []);
    } catch {
      toast.error("Erro ao carregar campanhas");
    } finally {
      setLoading(false);
    }
  }, [apiCall]);

  useEffect(() => { load(); }, [load]);

  // Auto-refresh while there are queued/active jobs
  useEffect(() => {
    const hasActive = jobs.some((j) => (j.queued || 0) > 0);
    if (!hasActive) return;
    const t = setInterval(load, 10000);
    return () => clearInterval(t);
  }, [jobs, load]);

  return (
    <div className="space-y-4" data-testid="wa-jobs-page">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate("/campanhas")}
            data-testid="back-to-campaigns"
          >
            <ArrowLeft className="h-4 w-4 mr-1" strokeWidth={1.5} />
            Voltar pra Campanhas
          </Button>
        </div>
        <Button variant="outline" size="sm" onClick={load} disabled={loading} data-testid="jobs-refresh">
          <RefreshCcw className={`h-3 w-3 mr-1 ${loading ? "animate-spin" : ""}`} strokeWidth={1.5} />
          Atualizar
        </Button>
      </div>

      <div>
        <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Comunicação</div>
        <h1 className="text-2xl font-light tracking-tight flex items-center gap-2">
          <MessageCircle className="h-5 w-5 text-emerald-700" strokeWidth={1.5} />
          Histórico de Disparos
        </h1>
        <p className="text-xs text-muted-foreground mt-1">
          Acompanhe o progresso das campanhas de WhatsApp em andamento e concluídas. Auto-atualiza enquanto há mensagens na fila.
        </p>
      </div>

      <Card className="border border-border bg-card shadow-soft rounded-md overflow-hidden">
        {loading ? (
          <div className="p-8 text-center text-sm text-muted-foreground">Carregando…</div>
        ) : jobs.length === 0 ? (
          <div className="p-8 text-center text-sm text-muted-foreground">
            Nenhuma campanha ainda. Abra <b>/clientes</b>, aplique um filtro (funil ou Dashboard) e clique em "Disparar WhatsApp".
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-secondary/40">
                <tr className="text-left text-[10px] uppercase tracking-widest text-muted-foreground">
                  <th className="px-3 py-2">Quando</th>
                  <th className="px-3 py-2">Total</th>
                  <th className="px-3 py-2">Progresso</th>
                  <th className="px-3 py-2">Status</th>
                  <th className="px-3 py-2"></th>
                </tr>
              </thead>
              <tbody>
                {jobs.map((j) => {
                  const completed = (j.sent || 0) + (j.failed || 0);
                  const pct = j.total > 0 ? Math.round((completed / j.total) * 100) : 0;
                  const isActive = (j.queued || 0) > 0;
                  return (
                    <tr key={j.job_id} className="border-t border-border/40 hover:bg-secondary/30 cursor-pointer" onClick={() => setSelected(j)} data-testid={`job-row-${j.job_id}`}>
                      <td className="px-3 py-2 text-xs">
                        {new Date(j.created_at).toLocaleString("pt-BR", { dateStyle: "short", timeStyle: "short" })}
                      </td>
                      <td className="px-3 py-2 text-xs">{j.total}</td>
                      <td className="px-3 py-2">
                        <div className="w-[200px]">
                          <div className="h-2 rounded-full bg-secondary overflow-hidden">
                            <div
                              className="h-full transition-all"
                              style={{
                                width: `${pct}%`,
                                background: isActive ? "#a16207" : "#15803d",
                              }}
                            />
                          </div>
                          <div className="text-[10px] text-muted-foreground mt-0.5">{completed}/{j.total} · {pct}%</div>
                        </div>
                      </td>
                      <td className="px-3 py-2 text-xs">
                        <div className="flex flex-wrap gap-1">
                          {j.sent > 0 && (
                            <Badge variant="outline" style={{ background: STATUS_COLOR.sent.bg, color: STATUS_COLOR.sent.fg, borderColor: "transparent" }} className="text-[10px]">
                              <CheckCircle2 className="h-2.5 w-2.5 mr-0.5" strokeWidth={1.5} />
                              {j.sent}
                            </Badge>
                          )}
                          {j.queued > 0 && (
                            <Badge variant="outline" style={{ background: STATUS_COLOR.queued.bg, color: STATUS_COLOR.queued.fg, borderColor: "transparent" }} className="text-[10px]">
                              <Clock className="h-2.5 w-2.5 mr-0.5" strokeWidth={1.5} />
                              {j.queued}
                            </Badge>
                          )}
                          {j.failed > 0 && (
                            <Badge variant="outline" style={{ background: STATUS_COLOR.failed.bg, color: STATUS_COLOR.failed.fg, borderColor: "transparent" }} className="text-[10px]">
                              <AlertCircle className="h-2.5 w-2.5 mr-0.5" strokeWidth={1.5} />
                              {j.failed}
                            </Badge>
                          )}
                        </div>
                      </td>
                      <td className="px-3 py-2 text-right">
                        <ChevronRight className="h-4 w-4 inline-block text-muted-foreground" strokeWidth={1.5} />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      <JobDetailDialog open={!!selected} job={selected} onClose={() => setSelected(null)} />
    </div>
  );
}

function JobDetailDialog({ open, job, onClose }) {
  const { apiCall } = useAuth();
  const [sends, setSends] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open || !job) return;
    setLoading(true);
    apiCall("get", `/whatsapp/campaigns/${job.job_id}/sends`)
      .then((r) => setSends(r.data?.sends || []))
      .catch(() => setSends([]))
      .finally(() => setLoading(false));
  }, [open, job, apiCall]);

  if (!job) return null;

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-3xl" data-testid="job-detail-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <MessageCircle className="h-4 w-4 text-emerald-700" strokeWidth={1.5} />
            Detalhe da campanha
          </DialogTitle>
          <DialogDescription className="text-xs">
            Status de envio individual de cada cliente neste job.
          </DialogDescription>
        </DialogHeader>

        <div className="grid grid-cols-4 gap-2 text-xs mb-3">
          <Stat label="Total" value={job.total} />
          <Stat label="Enviadas" value={job.sent || 0} color={STATUS_COLOR.sent.fg} />
          <Stat label="Na fila" value={job.queued || 0} color={STATUS_COLOR.queued.fg} />
          <Stat label="Falhas" value={job.failed || 0} color={STATUS_COLOR.failed.fg} />
        </div>

        <div className="max-h-[420px] overflow-y-auto border border-border rounded-md">
          {loading ? (
            <div className="p-4 text-center text-xs text-muted-foreground">Carregando envios…</div>
          ) : sends.length === 0 ? (
            <div className="p-4 text-center text-xs text-muted-foreground">Nenhum envio neste job.</div>
          ) : (
            <table className="w-full text-xs">
              <thead className="bg-secondary/40 sticky top-0">
                <tr className="text-left text-[10px] uppercase tracking-widest text-muted-foreground">
                  <th className="px-2 py-1">Cliente</th>
                  <th className="px-2 py-1">Telefone</th>
                  <th className="px-2 py-1">Status</th>
                  <th className="px-2 py-1">Quando</th>
                </tr>
              </thead>
              <tbody>
                {sends.map((s) => {
                  const st = STATUS_COLOR[s.status] || { bg: "#e5e7eb", fg: "#374151", label: s.status };
                  return (
                    <tr key={s.send_id} className="border-t border-border/40" data-testid={`send-row-${s.send_id}`}>
                      <td className="px-2 py-1">{s.client_name}</td>
                      <td className="px-2 py-1 text-muted-foreground">{s.phone}</td>
                      <td className="px-2 py-1">
                        <Badge variant="outline" style={{ background: st.bg, color: st.fg, borderColor: "transparent" }} className="text-[10px]">
                          {st.label}
                        </Badge>
                        {s.last_error && (
                          <span className="ml-1 text-[10px] text-amber-700" title={s.last_error}>
                            ({s.last_error.slice(0, 50)}…)
                          </span>
                        )}
                      </td>
                      <td className="px-2 py-1 text-muted-foreground">
                        {s.sent_at
                          ? new Date(s.sent_at).toLocaleTimeString("pt-BR")
                          : (s.failed_at ? new Date(s.failed_at).toLocaleTimeString("pt-BR") : "—")}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Fechar</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Stat({ label, value, color }) {
  return (
    <Card className="p-2 border border-border bg-card text-center shadow-soft rounded">
      <div className="text-[9px] uppercase tracking-widest text-muted-foreground">{label}</div>
      <div className="text-base font-medium" style={color ? { color } : {}}>{value}</div>
    </Card>
  );
}
